var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Checkbox, Divider, Grid, IconButton, LinearProgress, Menu, MenuItem, Pagination, Select, Skeleton, Stack, Typography, } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { listStyle } from "./list.style";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../utils/useDimensions";
import React, { useCallback, useEffect, useState } from "react";
import { CommonThemeColors } from "../theme";
import ColumnSelect from "./ColumnSelect";
import FilterSelect from "./FilterSelect";
import SearchInput from "./SearchInput";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { hexToRgba } from "../../utils/color.util";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
var ITEM_HEIGHT = 48;
var ITEM_PADDING_TOP = 8;
var MenuProps = {
    PaperProps: {
        style: {
            // maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};
var CList = function (props) {
    var _a, _b, _c;
    var _d = useState(props.searchQuery || ''), searchQuery = _d[0], setSearchQuery = _d[1];
    var t = useTranslation().t;
    var _f = useState(""), showingText = _f[0], setShowingText = _f[1];
    var pageCount = Math.ceil((props.totalElements || 1) / (props.size || 1));
    var pageNumbers = Array.from({ length: pageCount }, function (_, i) { return i + 1; });
    useEffect(function () {
        var _a;
        var from = props.page && props.size ? (props.page - 1) * props.size + 1 : 1;
        var to = from + (((_a = props.items) === null || _a === void 0 ? void 0 : _a.length) || 0) - 1;
        var showingText = props.totalElements ? t("Showing entries", {
            from: from,
            to: to,
            total: props.totalElements
        }) : t('No items found');
        if (props.page && props.onPageChange && pageCount < props.page) {
            props.onPageChange(1);
        }
        setShowingText(showingText);
    }, [pageNumbers, pageCount]);
    var _g = useDimensions(), width = _g.width, isSmallScreen = _g.isSmallScreen;
    var _h = React.useState(null), anchorEl = _h[0], setAnchorEl = _h[1];
    var open = Boolean(anchorEl);
    var handleOpenAdditionalClick = function (event) {
        setAnchorEl(event.currentTarget);
    };
    var handleOpenAdditionalClose = function () {
        setAnchorEl(null);
    };
    // Needed this callback as we delegated debouncing to SearchInput component
    var handleSearch = useCallback(function (query) {
        setSearchQuery(query);
        if (props.onSearch) {
            props.onSearch(query);
        }
    }, []);
    var getColumnStyle = function (column) {
        switch (column.width) {
            case "xs":
                return listStyle.cellXS;
            case "s":
                return listStyle.cellS;
            case "m":
                return listStyle.cellM;
            case "l":
                return listStyle.cellL;
            case "lg":
                return listStyle.cellLG;
            case "xl":
                return listStyle.cellXL;
            case 'xxl':
                return listStyle.cellXXL;
            case 'xxxl':
                return listStyle.cellXXXL;
            case 'grow':
                return listStyle.cellGrow;
        }
    };
    var renderColumnsConfigurator = function () {
        if (!props.allColumns)
            return null;
        return (_jsx(ColumnSelect, { columns: props.columns, allColumns: props.allColumns, onChange: function (columns) {
                var _a;
                (_a = props.setColumns) === null || _a === void 0 ? void 0 : _a.call(props, columns);
            } }));
    };
    var renderFilters = function () {
        if (!props.filterOptions)
            return null;
        return (_jsx(FilterSelect, { filterOptions: props.filterOptions, filterRules: props.filterRules, setFilterRules: props.setFilterRules, forceSectorId: props.forceSectorId }));
    };
    var renderSearch = function () {
        return (_jsxs(Box, __assign({ display: 'flex', sx: {
                background: '#fff',
                width: '100%',
                alignSelf: 'center',
                boxSizing: 'border-box',
                flexWrap: 'wrap',
                alignItems: { xs: 'stretch', sm: 'center' },
                marginBottom: props.searchActions ? '20px' : 0,
                columnGap: { "xs": 0, "md": 1 },
                rowGap: 1,
                position: 'relative',
            }, className: 'hide-on-print' }, { children: [_jsx(Box, __assign({ sx: {
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    } }, { children: !props.rightHandSideSearch && (_jsxs(_Fragment, { children: [props.onSearch && (_jsx(SearchInput, { initialQuery: searchQuery || '', onSearch: handleSearch, placeholder: t('Search'), loading: props.loading, debounceTime: 500 })), renderColumnsConfigurator(), renderFilters()] })) })), _jsx(Box, __assign({ sx: {
                        position: 'absolute',
                        left: '50%',
                        // Preventing the search bar from pushing other elements when opening
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        '& > *': { pointerEvents: 'auto' },
                        '& .MuiStack-root': {
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            rowGap: 1,
                        },
                        '& .MuiFormControl-root': {
                            width: 'auto',
                        },
                        '& .MuiButton-root': {
                            width: 'auto',
                        }
                    } }, { children: props.searchActions })), _jsxs(Box, __assign({ sx: {
                        flex: 1,
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                    } }, { children: [_jsx(Box, __assign({ sx: { flexShrink: 0 } }, { children: props.actionBar })), _jsx(IconButton, __assign({ id: "list-more-menu-item", "aria-expanded": open ? 'true' : undefined, onClick: handleOpenAdditionalClick }, { children: _jsx(MoreVertIcon, {}) }))] })), _jsxs(Menu, __assign({ id: "list-more-menu-item", anchorEl: anchorEl, open: open, onClose: handleOpenAdditionalClose, MenuListProps: {
                        'aria-labelledby': 'basic-button',
                    } }, { children: [_jsx(MenuItem, __assign({ onClick: function () {
                                var _a;
                                (_a = props.onChangeSort) === null || _a === void 0 ? void 0 : _a.call(props, 'id', 'DESC');
                                handleOpenAdditionalClose();
                            } }, { children: t('Reset sort') })), _jsx(MenuItem, __assign({ onClick: function () {
                                var _a;
                                (_a = props.onResetColumns) === null || _a === void 0 ? void 0 : _a.call(props);
                                handleOpenAdditionalClose();
                            } }, { children: t('Reset columns') })), _jsx(Divider, {}), _jsx(MenuItem, __assign({ onClick: function () {
                                var _a, _b;
                                (_a = props.onResetColumns) === null || _a === void 0 ? void 0 : _a.call(props);
                                (_b = props.onChangeSort) === null || _b === void 0 ? void 0 : _b.call(props, 'id', 'DESC');
                                handleOpenAdditionalClose();
                            } }, { children: t('Reset All') }))] })), 
                // This one is used only for lists where we have search on the RHS
                (props.onSearch && props.rightHandSideSearch) &&
                    _jsx(SearchInput, { initialQuery: searchQuery || '', onSearch: handleSearch, loading: props.loading, debounceTime: 500, 
                        // Immediately expand if the search trigger event happened and is open action
                        forceExpand: props.searchBarOpenTriggered, rightHandSideSearch: props.rightHandSideSearch, searchSidebarTrigger: props.searchBarOpenTriggered })] })));
    };
    var renderFloatingHeader = function (enhancedProps) {
        var _a, _b, _c;
        var _d = useState(null), draggedColumn = _d[0], setDraggedColumn = _d[1];
        var _f = useState(null), dragOverColumn = _f[0], setDragOverColumn = _f[1];
        if (width < 500 && props.renderMobileView)
            return null;
        var handleDragStart = function (e, columnId) {
            if (!enhancedProps.enableColumnDrag)
                return;
            setDraggedColumn(columnId);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', columnId);
            // Add visual feedback to the drag image
            var dragImage = e.currentTarget.cloneNode(true);
            dragImage.style.opacity = '0.8';
            e.dataTransfer.setDragImage(dragImage, 0, 0);
        };
        var handleDragOver = function (e, columnId) {
            if (!enhancedProps.enableColumnDrag || !draggedColumn)
                return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDragOverColumn(columnId);
        };
        var handleDragLeave = function (e) {
            // Only clear if we're actually leaving the column area
            if (!e.currentTarget.contains(e.relatedTarget)) {
                setDragOverColumn(null);
            }
        };
        var handleDrop = function (e, targetColumnId) {
            var _a;
            e.preventDefault();
            if (!enhancedProps.enableColumnDrag || !draggedColumn || draggedColumn === targetColumnId) {
                setDraggedColumn(null);
                setDragOverColumn(null);
                return;
            }
            var draggedIndex = props.columns.findIndex(function (col) { return col.id === draggedColumn; });
            var targetIndex = props.columns.findIndex(function (col) { return col.id === targetColumnId; });
            if (draggedIndex !== -1 && targetIndex !== -1) {
                var newColumns = __spreadArray([], props.columns, true);
                var removed = newColumns.splice(draggedIndex, 1)[0];
                newColumns.splice(targetIndex, 0, removed);
                // Call the reorder callback
                (_a = enhancedProps.onColumnsReorder) === null || _a === void 0 ? void 0 : _a.call(enhancedProps, newColumns);
            }
            setDraggedColumn(null);
            setDragOverColumn(null);
        };
        var handleDragEnd = function () {
            setDraggedColumn(null);
            setDragOverColumn(null);
        };
        var handleColumnClick = function (column) {
            var _a, _b, _c;
            // Don't trigger sorting if we're in the middle of a drag operation
            if (draggedColumn)
                return;
            if (!!searchQuery || !column.sortProp)
                return;
            if (props.sortProp === column.sortProp) {
                if (props.sortDir === 'ASC') {
                    (_a = props.onChangeSort) === null || _a === void 0 ? void 0 : _a.call(props, props.sortProp, 'DESC');
                }
                else {
                    (_b = props.onChangeSort) === null || _b === void 0 ? void 0 : _b.call(props, props.sortProp, 'ASC');
                }
            }
            else {
                (_c = props.onChangeSort) === null || _c === void 0 ? void 0 : _c.call(props, column.sortProp, column.initialSortDir || 'ASC');
            }
        };
        return (_jsxs(Box, __assign({ sx: __assign({ display: 'flex', height: 40, minWidth: 'fit-content', width: '100%', borderBottom: "1px solid ".concat(CommonThemeColors.ref.neutral.neutral80) }, props.headerStyle), id: "c-list-columns" }, { children: [props.multiselect && (_jsx(Checkbox, { checked: !!((_a = props.items) === null || _a === void 0 ? void 0 : _a.length) && ((_b = props.selected) === null || _b === void 0 ? void 0 : _b.size()) === ((_c = props.items) === null || _c === void 0 ? void 0 : _c.length), onChange: props.onSelectAll, className: "hide-on-print" })), props.columns.map(function (column) {
                    var isDragged = draggedColumn === column.id;
                    var isDragOver = dragOverColumn === column.id && draggedColumn !== column.id;
                    var canSort = !searchQuery && !!column.sortProp;
                    var isCurrentSort = props.sortProp === column.sortProp;
                    return (_jsxs(Box, __assign({ "data-column-id": column.id, draggable: enhancedProps.enableColumnDrag, onDragStart: function (e) { return handleDragStart(e, column.id); }, onDragOver: function (e) { return handleDragOver(e, column.id); }, onDragLeave: handleDragLeave, onDrop: function (e) { return handleDrop(e, column.id); }, onDragEnd: handleDragEnd, style: __assign(__assign(__assign({}, listStyle.cell), getColumnStyle(column)), { cursor: enhancedProps.enableColumnDrag
                                ? 'grab'
                                : (canSort ? 'pointer' : 'default'), opacity: isDragged ? 0.5 : 1, transition: 'all 0.2s ease', borderLeft: isDragOver ? '3px solid #1976d2' : 'none', backgroundColor: isDragOver ? 'rgba(25, 118, 210, 0.08)' : 'transparent', position: 'relative' }), display: 'flex', alignItems: 'center', onClick: function () { return handleColumnClick(column); }, sx: {
                            '&:active': enhancedProps.enableColumnDrag ? { cursor: 'grabbing' } : {},
                            userSelect: 'none',
                            '@media print': column.hideOnPrint ? { display: 'none' } : {},
                        } }, { children: [enhancedProps.enableColumnDrag && (_jsx(DragIndicatorIcon, { sx: {
                                    fontSize: 16,
                                    color: 'rgba(0, 0, 0, 0.4)',
                                    mr: 0.5,
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' }
                                }, className: "hide-on-print" })), _jsx(Typography, __assign({ variant: 'body1', fontWeight: '600', sx: {
                                    flexGrow: 1,
                                    pointerEvents: enhancedProps.enableColumnDrag ? 'none' : 'auto'
                                } }, { children: column.name })), !searchQuery && canSort && isCurrentSort && props.sortDir === 'DESC' && (_jsx(ArrowDropDownIcon, { className: "hide-on-print" })), !searchQuery && canSort && isCurrentSort && props.sortDir === 'ASC' && (_jsx(ArrowDropUpIcon, { className: "hide-on-print" })), isDragOver && (_jsx(Box, { sx: {
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: 2,
                                    height: '100%',
                                    backgroundColor: '#1976d2',
                                    zIndex: 10
                                } }))] }), column.id));
                })] })));
    };
    var renderLoadingUI = function () {
        return __spreadArray([], Array(10), true).map(function (_, index) { return (_jsxs(Box, __assign({ display: "flex", sx: listStyle.row }, { children: [props.multiselect && (_jsx(Box, __assign({ width: 36, display: "flex", alignItems: "center", justifyContent: "center" }, { children: _jsx(Skeleton, { variant: "rectangular", width: 18, height: 18 }) }))), props.columns.map(function (column) { return (_jsx(Box, __assign({ sx: __assign(__assign({}, listStyle.cell), getColumnStyle(column)), display: "flex", alignItems: "center" }, { children: _jsx(Skeleton, { variant: "rectangular", width: "70%", height: 20 }) }), column.id)); })] }), index)); });
    };
    var renderProgressBar = function () {
        return _jsx(Box, __assign({ sx: {
                position: 'relative',
                height: 0,
            }, className: "hide-on-print" }, { children: _jsx(LinearProgress, { sx: {
                    position: 'absolute',
                    width: '100%',
                } }) }));
    };
    var renderEmptyState = function () { return (props.emptyComponent || (_jsx(Typography, __assign({ variant: "body2", color: "text.secondary", sx: { textAlign: 'center', py: 4 } }, { children: t('No items found') })))); };
    var renderItemsList = function () {
        var _a;
        if (!((_a = props.items) === null || _a === void 0 ? void 0 : _a.length)) {
            return renderEmptyState();
        }
        return props.items.map(function (item, index) {
            var _a, _b, _c, _d, _f, _g;
            if (width < 500 && props.renderMobileView) {
                return (_jsx(Box, __assign({ onClick: function () { var _a; return (_a = props.onItemClick) === null || _a === void 0 ? void 0 : _a.call(props, item, index); } }, { children: props.renderMobileView(item, index, props.items || []) }), ((_a = props.extractKey) === null || _a === void 0 ? void 0 : _a.call(props, item)) || item.id));
            }
            return (_jsxs(Box, __assign({ display: "flex", alignItems: "center", style: __assign(__assign(__assign(__assign({}, listStyle.row), (_b = props.styles) === null || _b === void 0 ? void 0 : _b.row), (_c = props.rowStyle) === null || _c === void 0 ? void 0 : _c.call(props, item, index, props.items || [])), { cursor: props.onItemClick ? "pointer" : "default", "&:hover": {
                        backgroundColor: hexToRgba(CommonThemeColors.sys.primary.main, 0.025),
                    } }), onClick: function () { var _a; return (_a = props.onItemClick) === null || _a === void 0 ? void 0 : _a.call(props, item, index); }, className: "lst-item-row" }, { children: [props.multiselect && (_jsx(Checkbox, { checked: (_d = props.selected) === null || _d === void 0 ? void 0 : _d.has(item), onClick: function (e) { return e.stopPropagation(); }, onChange: function () { var _a; return (_a = props.onSelect) === null || _a === void 0 ? void 0 : _a.call(props, item); } })), props.columns.map(function (column) {
                        var _a, _b;
                        var content = (_a = props.renderCell) === null || _a === void 0 ? void 0 : _a.call(props, item, column, index, props.items || []);
                        var contentClean = typeof content === "string" || typeof content === "number" ? (_jsx(Typography, __assign({ variant: "body1" }, { children: content }))) : (content);
                        return (_jsx(Box, __assign({ "data-column-id": column.id, "data-testid": "column-".concat(column.id), "data-ignore-on-print": column.hideOnPrint ? 'true' : undefined, sx: __assign(__assign(__assign(__assign({}, listStyle.cell), getColumnStyle(column)), (_b = props.styles) === null || _b === void 0 ? void 0 : _b.cell), { cursor: props.onItemClick ? "pointer" : "default", '@media print': column.hideOnPrint ? { display: 'none' } : {} }), display: "flex", alignItems: "center" }, { children: contentClean }), column.id));
                    }), (_f = props.renderRowAdditionalContent) === null || _f === void 0 ? void 0 : _f.call(props, item, index, props.items || [])] }), ((_g = props.extractKey) === null || _g === void 0 ? void 0 : _g.call(props, item)) || item.id));
        });
    };
    var renderFloatingFooter = function () {
        return (_jsx(Box, __assign({ justifyContent: 'center', alignItems: 'center', sx: __assign(__assign({}, listStyle.footer), { display: 'flex', flexDirection: 'column', gap: 2, width: "100%", borderTop: "1px solid ".concat(CommonThemeColors.ref.neutral.neutral80), paddingLeft: 0, paddingRight: 0 }), className: "hide-on-print" }, { children: !!props.page && !!props.totalPages &&
                _jsx(_Fragment, { children: _jsxs(Grid, __assign({ container: true, spacing: 2, alignItems: "center", justifyContent: "space-between", sx: {
                            width: "100%",
                            flexDirection: "column",
                            paddingTop: 1,
                            '@media (min-width:940px)': {
                                flexDirection: "row",
                                paddingTop: 0,
                                marginLeft: 0,
                            },
                        } }, { children: [_jsx(Grid, __assign({ item: true, sx: {
                                    order: 0,
                                    textAlign: "center",
                                    paddingBottom: 1,
                                    '@media (min-width:940px)': {
                                        order: 0,
                                        textAlign: "left",
                                        paddingBottom: 0
                                    },
                                } }, { children: _jsx(Typography, __assign({ variant: "subtitle1", noWrap: true }, { children: showingText })) })), _jsx(Grid, __assign({ item: true, sx: {
                                    order: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                } }, { children: _jsx(Pagination, { page: props.page, count: props.totalPages, onChange: function (_e, page) { var _a; return (_a = props.onPageChange) === null || _a === void 0 ? void 0 : _a.call(props, page); }, color: "primary", boundaryCount: isSmallScreen ? 0 : 1, siblingCount: 1 }) })), _jsx(Grid, __assign({ item: true, sx: {
                                    order: 2,
                                    display: "flex",
                                    justifyContent: "center",
                                    '@media (min-width:940px)': {
                                        justifyContent: "flex-end",
                                    },
                                } }, { children: _jsxs(Stack, __assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [_jsx(Typography, __assign({ variant: "subtitle1" }, { children: t('Page') })), _jsx(Select, __assign({ size: "small", value: props.page, onChange: function (e) { var _a; return (_a = props.onPageChange) === null || _a === void 0 ? void 0 : _a.call(props, e.target.value); }, MenuProps: {
                                                PaperProps: { style: { maxHeight: 200 } },
                                            } }, { children: pageNumbers.map(function (page) { return (_jsx(MenuItem, __assign({ value: page }, { children: page }), page)); }) })), _jsx(Typography, __assign({ variant: "subtitle1", noWrap: true }, { children: t('Results') })), _jsx(Select, __assign({ value: props.size, onChange: function (e) { var _a; return (_a = props.onSizeChange) === null || _a === void 0 ? void 0 : _a.call(props, e.target.value); }, size: "small" }, { children: (props.customPageSizes || [10, 25, 50]).map(function (size) { return (_jsx(MenuItem, __assign({ value: size }, { children: size }), size)); }) }))] })) }))] })) }) })));
    };
    return (_jsxs(Box, __assign({ ref: props.contentRef, id: props.id, className: (_a = props.classes) === null || _a === void 0 ? void 0 : _a.root, sx: __assign({ flexDirection: 'column', position: 'relative', marginBottom: '100px', '@media print': {
                display: 'block'
            } }, (_b = props.styles) === null || _b === void 0 ? void 0 : _b.root) }, { children: [props.printHeader, renderSearch(), _jsxs(Box, __assign({ sx: {
                    flexDirection: 'column',
                    width: '100%',
                    overflow: 'auto',
                    alignSelf: 'center',
                    boxSizing: 'border-box',
                    overflowX: 'auto',
                    '@media print': {
                        display: 'block',
                        overflow: 'hidden',
                        paddingBottom: "16px"
                    }
                } }, { children: [renderFloatingHeader({
                        onColumnsReorder: props.setColumns,
                        enableColumnDrag: true
                    }), props.loading && renderProgressBar(), props.loading && !((_c = props.items) === null || _c === void 0 ? void 0 : _c.length) ? renderLoadingUI() : renderItemsList()] })), renderFloatingFooter(), props.printFooter] })));
};
export default CList;
